import React from 'react';
import { Flex, JoyKitThemeProvider, TextV2, TooltipV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { Info } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { useSoldOutMessage } from './useSoldOutMessage';
import type { Maybe } from '@graphql/generated-base';

type SoldOutTagProps = Readonly<{
  eventDate?: Maybe<Date>;
}>;

/**
 * A tag that displays a message about a hotel being sold out,
 * with a tooltip hint about minimum stay requirements.
 */
export const SoldOutTag = ({ eventDate }: SoldOutTagProps) => {
  const { tq } = useTranslation('sharedComponents');
  const { message, tooltip } = useSoldOutMessage(eventDate);

  return (
    <JoyKitThemeProvider>
      <TooltipV2
        isOpen={tooltip ? undefined : false}
        overrides={{
          Content: { props: { paddingY: 4, paddingLeft: pxToRem(14), paddingRight: 4, borderRadius: pxToRem(12), color: 'white', marginLeft: 2 } },
        }}
        content={
          tooltip ? (
            <Flex
              flexDirection="column"
              maxWidth={pxToRem(300)}
            >
              <TextV2
                typographyVariant="label2"
                color="mono4"
              >
                {tooltip}
              </TextV2>
            </Flex>
          ) : undefined
        }
        placement="top"
        onMouseEnterDelay={500}
      >
        <Flex
          width={'100%'}
          gap={4}
          alignItems="center"
          border={'1px solid'}
          borderColor="info5"
          color="info5"
          borderRadius={pxToRem(8)}
          padding={4}
        >
          <Info
            size={32}
            title={tq`Hotel Availability`}
            aria-label={tq`Hotel Availability`}
          />
          <TextV2
            typographyVariant="body1"
            color="mono12"
          >
            {message}
          </TextV2>
        </Flex>
      </TooltipV2>
    </JoyKitThemeProvider>
  );
};
